import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { Popover as HeadlessPopover } from '@headlessui/react';

import { cn } from 'lib/helpers/utility';

import ErrorBoundary from 'components/shared/Error/Boundary';
import { usePopoverContext } from 'components/shared/Popovers/Popover/context/PopoverContext';
import Header from 'components/shared/shared/Header';

import PopoverButton from './components/Button/PopoverButton';
import PopoverPanel from './components/Panel/PopoverPanel';
import { useActiveOptionScroll } from './hooks/useActiveOptionScroll';
import { usePopoverHandlers } from './hooks/usePopoverHandlers';
import { usePopoverPosition } from './hooks/usePopoverPosition';

/**
 * @type {import('react').ForwardRefExoticComponent<import('./lib/propTypes').PopoverProps & import('react').RefAttributes<HTMLElement>>}
 */

const Popover = forwardRef((props, ref) => {
  const {
    position = 'right',
    isFixed = false,
    options = [],
    icon = 'context-menu-horizontal',
    iconSize = 16,
    iconColor,
    iconClassName = '',
    iconStroke = false,
    iconRight = '',
    iconRightColor,
    iconRightSize = 16,
    iconsShade = 700,
    iconRightClassName = '',
    optionIconSize = 16,
    optionIconColor = 'neutral',
    optionArrowIcon = 'chevron-right-linear',
    optionArrowIconStroke = false,
    optionArrowColor = 'neutral',
    optionActiveIcon = 'check-linear',
    optionsGroupBy,
    optionGroupLabelClassName = '',
    text = '',
    loading = false,
    disabled,
    disableBlur = false,
    className = '',
    buttonClassName = '',
    buttonTheme = null,
    buttonThemeType = 'secondary',
    textClassName = '',
    panelClassName = '',
    innerWrapperClassName = '',
    activeClassNames = {},
    isDropdown = false,
    isSeparator = false,
    isBorder = null,
    rerender = '',
    shouldCloseOnHover = true,
    dataQa = 'popover-button',
    id = null,
    onScroll = () => {},
    panelRef: outsidePanelRef = null,
    children,
    tooltip,
    labelTooltip,
    isTooltipDark = true,
    tooltipPlacement = 'bottom',
    customButton,
    customButtonTrigger,
    customButtonWrapperClassName,
    customButtonClassName,
    onOptionClick,
    onButtonClick,
    optionIconNoColor = false,
    onPanelMouseLeave,
    removeWidthAndHeight = false,
    ...headerProps
  } = props;

  const timeoutRef = useRef(null);
  const submenuRefs = useRef({});

  const popoverContext = usePopoverContext();
  const closeRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndexes, setHoveredIndexes] = useState({});

  const {
    panelFixedPosition,
    buttonRef,
    panelRef: positioningPanelRef,
    handlePanelRef,
    updatePopoverPosition,
    isPositioned
  } = usePopoverPosition({
    isFixed,
    position,
    rerender,
    isOpen,
    onClose: () => closeRef.current?.()
  });

  const { activeOptionRef } = useActiveOptionScroll({
    isOpen,
    panelRef: outsidePanelRef || positioningPanelRef
  });

  const panelRef = outsidePanelRef || positioningPanelRef;

  useImperativeHandle(ref, () => ({ buttonRef }));

  const { handleMouseEnter, handleMouseLeave, handleOptionClick, onPopoverClose } =
    usePopoverHandlers({
      setIsOpen,
      setHoveredIndexes,
      timeoutRef,
      shouldCloseOnHover,
      popoverContext,
      onOptionClick,
      disableBlur,
      panelRef,
      updatePopoverPosition,
      submenuRefs
    });

  const buttonProps = {
    buttonRef,
    onButtonClick,
    dataQa,
    buttonTheme,
    text,
    isSeparator,
    isBorder,
    buttonClassName,
    activeClassNames,
    disabled,
    buttonThemeType,
    loading,
    icon,
    iconSize,
    iconColor,
    iconsShade,
    iconStroke,
    iconClassName,
    textClassName,
    iconRight,
    iconRightSize,
    iconRightColor,
    iconRightClassName,
    isDropdown,
    tooltip,
    isTooltipDark,
    tooltipPlacement,
    customButton,
    customButtonTrigger,
    customButtonWrapperClassName,
    customButtonClassName,
    setIsOpen,
    removeWidthAndHeight,
    onOpen: () => setHoveredIndexes({})
  };

  const panelProps = {
    isFixed,
    handlePanelRef,
    panelRef,
    onScroll: (e) => (setHoveredIndexes({}), onScroll?.(e)),
    panelClassName,
    position,
    panelFixedPosition,
    isPositioned,
    options,
    optionsGroupBy,
    optionGroupLabelClassName,
    hoveredIndexes,
    handleMouseEnter,
    handleMouseLeave,
    handleOptionClick,
    activeOptionRef,
    submenuRefs,
    text,
    customButton,
    optionIconSize,
    optionIconColor,
    iconsShade,
    optionArrowIcon,
    optionArrowIconStroke,
    optionActiveIcon,
    optionArrowColor,
    optionIconNoColor,
    onPanelMouseLeave
  };

  return (
    <ErrorBoundary>
      <HeadlessPopover
        id={id}
        className={cn('w-fit', className)}
        onClose={onPopoverClose}
        onBlur={onPopoverClose}>
        {({ open, close }) => {
          closeRef.current = close;
          return (
            <>
              <Header {...headerProps} transcribing={false} tooltip={labelTooltip} />
              <div className={cn('relative', innerWrapperClassName)}>
                <PopoverButton {...buttonProps} closePopover={close} open={open} />
                <PopoverPanel {...panelProps} close={close}>
                  {children}
                </PopoverPanel>
              </div>
            </>
          );
        }}
      </HeadlessPopover>
    </ErrorBoundary>
  );
});

export default Popover;
